import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchForm from '../components/SearchForm';
import CandidateProfile from '../components/CandidateProfile';
import FreeQuotaBanner from '../components/FreeQuotaBanner';
import UpgradeModal from '../components/UpgradeModal';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { IntegrityScore } from '../components/IntegrityScore';
import { SkeletonList } from '../components/Skeletons';
import { useAuth } from '../contexts/AuthContext';
import FeatureCards from '../components/nav/FeatureCards';
import {
  Search, AlertCircle, Shield, BarChart3, Eye, FileText,
  ChevronRight, Users, Globe, Database, Zap, Lock, CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [showApp, setShowApp] = useState(false);
  const [quotaRefresh, setQuotaRefresh] = useState(0);
  const [upgrade, setUpgrade] = useState({ open: false, reason: '' });
  const [consultasTotal, setConsultasTotal] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const resultRef = useRef(null);

  // Scroll to result whenever an analysis is set (esp. helpful on mobile where
  // result renders below the form instead of side-by-side).
  useEffect(() => {
    if (currentAnalysis && resultRef.current) {
      // Small delay to let the DOM render the result card
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [currentAnalysis]);

  const loadRecentCandidates = useCallback(async () => {
    try {
      setFetchError(false);
      const response = await axios.get(`${API}/candidates`, { timeout: 15000 });
      setRecentCandidates(response.data.slice(0, 6));
    } catch (error) {
      console.error('Error loading recent candidates:', error);
      setFetchError(true);
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  useEffect(() => {
    loadRecentCandidates();
  }, [loadRecentCandidates]);

  // Bloco 6 — Live counter for the landing hero. Polls /consultas/count every 10s.
  useEffect(() => {
    let cancelled = false;
    const fetchTotal = async () => {
      try {
        const res = await fetch(`${API}/public/consultas/count`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) setConsultasTotal(json?.total ?? null);
      } catch { /* silent — counter is decorative */ }
    };
    fetchTotal();
    const id = setInterval(fetchTotal, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (location.state?.candidate) {
      setCurrentAnalysis(location.state.candidate);
      setShowApp(true);
    }
    const params = new URLSearchParams(location.search);
    if (params.get('analyze')) {
      setShowApp(true);
    }
  }, [location.state, location.search]);

  const handleAnalysisComplete = (analysis) => {
    setCurrentAnalysis(analysis);
    loadRecentCandidates();
    setQuotaRefresh((k) => k + 1);
  };

  const handleStartAnalysis = () => {
    setShowApp(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // If user came to analyze or already started, show the app directly
  if (showApp || currentAnalysis) {
    return (
      <div className="space-y-6 md:space-y-8">
        <div className="text-center py-4 md:py-10">
          <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-[#0F1C2E] tracking-tight mb-2" data-testid="home-title">
            O que descobrimos sobre esse político
          </h1>
          <p className="text-sm sm:text-base text-[#475569] max-w-2xl mx-auto px-2">
            Um resumo simples, com nota de integridade e fontes oficiais citadas.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-1 space-y-4">
            {user?.must_change_password && (
              <div
                className="rounded-md border border-[#FCD34D] bg-[#FFFBEB] px-3 py-2.5 flex items-start gap-2"
                data-testid="must-change-password-banner"
              >
                <AlertCircle className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" />
                <div className="flex-1 text-[12px] text-[#92400E] leading-snug">
                  <strong>Sua senha foi redefinida.</strong> Recomendamos alterar agora em{' '}
                  <button
                    onClick={() => navigate('/perfil')}
                    className="underline font-bold hover:text-[#78350F]"
                    data-testid="must-change-password-cta"
                  >Perfil</button>.
                </div>
              </div>
            )}
            <FreeQuotaBanner refreshKey={quotaRefresh} />
            <SearchForm
              onAnalysisComplete={handleAnalysisComplete}
              onUpgradeRequired={(reason) => setUpgrade({ open: true, reason: reason || '' })}
            />
            <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md">
              <h3 className="text-base font-bold text-[#0F1C2E] mb-4">Análises Recentes</h3>
              {loadingRecent && <SkeletonList count={3} />}
              {!loadingRecent && fetchError && (
                <div className="text-center py-6" data-testid="recent-error">
                  <AlertCircle className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
                  <p className="text-sm text-[#94A3B8]">Erro ao carregar dados</p>
                  <button onClick={loadRecentCandidates} className="text-sm text-[#006030] underline mt-2">Tentar novamente</button>
                </div>
              )}
              {!loadingRecent && !fetchError && recentCandidates.length === 0 && (
                <div className="text-center py-6" data-testid="recent-empty">
                  <Search className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
                  <p className="text-sm text-[#94A3B8]">Nenhuma análise ainda</p>
                </div>
              )}
              {!loadingRecent && !fetchError && recentCandidates.length > 0 && (
                <div className="space-y-2">
                  {recentCandidates.map((candidate) => (
                    <div key={candidate.id} data-testid={`recent-candidate-${candidate.id}`}
                      onClick={() => setCurrentAnalysis(candidate)}
                      className={`p-3 border rounded-md cursor-pointer transition-all duration-200 hover:border-[#006030] hover:shadow-sm ${
                        currentAnalysis?.id === candidate.id ? 'border-[#006030] bg-[#E8F5E9]' : 'border-[#E2E8F0]'
                      }`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#0F1C2E] text-sm truncate">{candidate.name}</p>
                          <p className="text-xs text-[#94A3B8] truncate">{candidate.position} - {candidate.state}</p>
                        </div>
                        <div className="shrink-0"><IntegrityScore score={candidate.integrity_score} size="sm" /></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
          <div className="lg:col-span-2" ref={resultRef}>
            {currentAnalysis ? (
              <CandidateProfile candidate={currentAnalysis} onBack={() => setCurrentAnalysis(null)} />
            ) : (
              <Card className="p-8 md:p-12 bg-white border border-[#E2E8F0] rounded-md text-center" data-testid="empty-analysis">
                <div className="max-w-sm mx-auto">
                  <div className="w-16 h-16 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-5">
                    <Search className="w-8 h-8 text-[#006030]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F1C2E] mb-2">Pesquise um Político</h3>
                  <p className="text-sm text-[#475569]">Digite o nome ou o número de urna ao lado. A gente busca os dados oficiais pra você.</p>
                </div>
              </Card>
            )}
          </div>
        </div>
        <UpgradeModal
          open={upgrade.open}
          reason={upgrade.reason}
          onClose={() => setUpgrade({ open: false, reason: '' })}
        />
      </div>
    );
  }

  // Landing page for new visitors
  return (
    <div className="space-y-0" data-testid="landing-page">
      {/* Hero Section — Bloco 6 (humanised copy) */}
      <section className="relative py-10 md:py-20 px-4 text-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E8F5E9]/50 to-transparent pointer-events-none" />
        <div className="relative max-w-3xl mx-auto">
          <img src="/logo.png" alt="Tô de Olho" className="h-20 md:h-24 mx-auto mb-6 rounded-2xl shadow-lg" />
          <h1
            className="text-3xl sm:text-4xl lg:text-6xl font-black text-[#0F1C2E] tracking-tight leading-[1.1] mb-4"
            data-testid="hero-headline"
          >
            <span>A plataforma brasileira de </span>
            <span className="text-[#006030]">inteligência pública.</span>
          </h1>
          <p
            className="text-base sm:text-lg text-[#475569] max-w-xl mx-auto mb-8 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Transformamos dados oficiais em informação simples para todo cidadão. Consulte candidatos, empresas e municípios em segundos.
          </p>
          <Button
            onClick={handleStartAnalysis}
            className="bg-[#006030] hover:bg-[#004020] text-white font-bold h-14 px-10 text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid="hero-cta-btn"
          >
            <Search className="w-5 h-5 mr-2" /> Consultar agora, é grátis
          </Button>
          <p className="text-xs text-[#94A3B8] mt-3">Sem cartão. Resposta em até 1 minuto.</p>
          {consultasTotal !== null && consultasTotal > 0 && (
            <p
              className="text-sm text-[#006030] font-semibold mt-5"
              data-testid="hero-live-counter"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-[#006030] mr-2 align-middle animate-pulse" />
              Mais de {consultasTotal.toLocaleString('pt-BR')} consulta{consultasTotal === 1 ? '' : 's'} realizada{consultasTotal === 1 ? '' : 's'}
            </p>
          )}
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-6 px-4 border-y border-[#E2E8F0] bg-white" data-testid="trust-bar">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {[
            { icon: Database, label: 'Dados oficiais do TSE' },
            { icon: Globe, label: 'Câmara dos Deputados' },
            { icon: FileText, label: 'ALESP e Senado' },
            { icon: Shield, label: 'Em conformidade com a LGPD' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-[#475569]">
              <item.icon className="w-4 h-4 text-[#006030]" />
              <span className="text-xs font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Cards — "O que você quer descobrir?" (Bloco 4) */}
      <FeatureCards />

      {/* Frase de Impacto */}
      <section className="py-10 md:py-16 px-4 bg-[#006030] text-center" data-testid="impact-section">
        <div className="max-w-2xl mx-auto">
          <p className="text-xl sm:text-2xl md:text-3xl font-black text-white leading-snug">
            &ldquo;Democracia se fortalece quando o eleitor tem acesso à informação certa, no momento certo.&rdquo;
          </p>
          <p className="text-sm text-white/60 mt-4">Tô de Olho · Informação cidadã baseada em dados</p>
        </div>
      </section>

      {/* Como Funciona — Bloco 6 (3 humanised steps) */}
      <section className="py-10 md:py-16 px-4 bg-white" data-testid="how-it-works">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-base sm:text-lg font-black text-[#0F1C2E] tracking-wider mb-2">Como funciona</h2>
          <p className="text-sm text-[#94A3B8] mb-10">Três passos simples, do celular, em minutos</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '01', icon: Search, title: 'Você busca o candidato',
                desc: 'Digite o nome ou o número de urna. Funciona para presidente, governador, deputado, senador e vereador.',
              },
              {
                step: '02', icon: BarChart3, title: 'A gente analisa os dados oficiais',
                desc: 'Cruzamos TSE, TCU e Portal da Transparência. Você recebe um resumo simples, com nota de integridade e fontes citadas.',
              },
              {
                step: '03', icon: Eye, title: 'Você compartilha com quem importa',
                desc: 'Manda no WhatsApp, posta no Instagram, tuita. Uma imagem pronta pra você conversar sobre política com a família.',
              },
            ].map((item, i) => (
              <div key={i} className="relative p-6 rounded-xl border border-[#E2E8F0] bg-[#FAFBFC] hover:border-[#006030] hover:shadow-md transition-all duration-300 group" data-testid={`how-step-${i+1}`}>
                <span className="absolute -top-3 left-5 bg-[#006030] text-white text-[10px] font-black px-3 py-1 rounded-full">{item.step}</span>
                <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#006030] transition-colors duration-300">
                  <item.icon className="w-6 h-6 text-[#006030] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-sm font-bold text-[#0F1C2E] mb-2">{item.title}</h3>
                <p className="text-xs text-[#475569] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prova Social / Números */}
      <section className="py-10 md:py-16 px-4 bg-[#F4F6F8]" data-testid="social-proof">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-base sm:text-lg font-black text-[#0F1C2E] tracking-wider mb-8">Dados que sustentam a plataforma</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { number: '500+', label: 'Deputados federais monitorados', icon: Users },
              { number: '94', label: 'Deputados estaduais (SP)', icon: Globe },
              { number: '81', label: 'Senadores em exercício', icon: Shield },
              { number: '5.500+', label: 'Candidatos no TSE (2024)', icon: Database },
            ].map((item, i) => (
              <Card key={i} className="p-4 bg-white border border-[#E2E8F0] rounded-xl text-center hover:shadow-md transition-shadow">
                <item.icon className="w-5 h-5 text-[#006030] mx-auto mb-2" />
                <p className="text-2xl md:text-3xl font-black text-[#006030]">{item.number}</p>
                <p className="text-[10px] text-[#94A3B8] mt-1 leading-tight">{item.label}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-10 md:py-16 px-4 bg-white" data-testid="differentials">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-base sm:text-lg font-black text-[#0F1C2E] tracking-wider mb-2">O que nos diferencia</h2>
            <p className="text-sm text-[#94A3B8]">Não somos apenas um agregador de dados</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                icon: Zap, title: 'Inteligência Artificial aplicada',
                desc: 'Cruzamos dados de múltiplas fontes e usamos IA para gerar um score de integridade explicável, com análise jurídica, patrimonial e política.',
              },
              {
                icon: BarChart3, title: 'Gastos públicos em tempo real',
                desc: 'Despesas da Câmara dos Deputados e Assembleia Legislativa de SP com categorias, fornecedores e alertas automáticos de padrões atípicos.',
              },
              {
                icon: Lock, title: 'Neutralidade e conformidade legal',
                desc: 'Não fazemos acusações. Apresentamos dados públicos com linguagem neutra, educativa e em conformidade com a LGPD.',
              },
              {
                icon: CheckCircle2, title: 'Interpretação acessível',
                desc: 'Transformamos dados técnicos em linguagem simples. Qualquer eleitor pode entender processos, patrimônio e gastos.',
              },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-xl border border-[#E2E8F0] hover:border-[#006030]/30 hover:bg-[#FAFBFC] transition-all duration-300">
                <div className="w-10 h-10 bg-[#E8F5E9] rounded-lg flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-[#006030]" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#0F1C2E] mb-1">{item.title}</h3>
                  <p className="text-xs text-[#475569] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Análises Recentes (prova de uso) */}
      {recentCandidates.length > 0 && (
        <section className="py-10 md:py-16 px-4 bg-[#F4F6F8]" data-testid="recent-analyses-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-base sm:text-lg font-black text-[#0F1C2E] tracking-wider mb-6">Análises recentes na plataforma</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {recentCandidates.slice(0, 6).map((c) => (
                <Card key={c.id} onClick={() => { setCurrentAnalysis(c); setShowApp(true); }}
                  className="p-4 bg-white border border-[#E2E8F0] rounded-xl cursor-pointer hover:border-[#006030] hover:shadow-md transition-all duration-300 text-left"
                  data-testid={`landing-candidate-${c.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-bold text-[#0F1C2E] text-sm truncate flex-1">{c.name}</p>
                    <IntegrityScore score={c.integrity_score} size="sm" />
                  </div>
                  <p className="text-[10px] text-[#94A3B8]">{c.position} · {c.state}</p>
                  <div className="flex items-center gap-1 mt-2 text-[10px] text-[#006030] font-medium">
                    <span>Ver análise completa</span> <ChevronRight className="w-3 h-3" />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Final — Bloco 6 (humanised copy) */}
      <section className="py-12 md:py-20 px-4 bg-[#006030] text-center" data-testid="cta-section">
        <div className="max-w-xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-3">Comece grátis, sem cartão</h2>
          <p className="text-sm text-white/70 mb-8">
            Tô de Olho: informação pública simples, no celular, para você acompanhar quem decide por você.
          </p>
          <Button
            onClick={handleStartAnalysis}
            className="bg-[#F0D000] hover:bg-[#D4B800] text-[#0F1C2E] font-bold h-14 px-10 text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid="final-cta-btn"
          >
            <Search className="w-5 h-5 mr-2" /> Consultar um político agora
          </Button>
          {!user && (
            <p className="text-xs text-white/50 mt-4">
              Quer consultar quantos políticos quiser?{' '}
              <span onClick={() => navigate('/planos')} className="text-[#F0D000] underline cursor-pointer hover:text-white">Conheça o acesso completo</span>
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
